import * as Yup from 'yup';
import { PasswordRegex, DobRegex, UrlRegex, NumberRegex, BankRoutingNumberRegex, PhoneRegexAlt, cardExpirationDateRegex, E164PhoneRegex, EmailRegex } from './regex';
import { PASSWORD_REQUIREMENTS } from './constants';

export const schemaValidationUser = Yup.object({
  firstName: Yup.string().max(100, 'Too Long!').required('Required First Name'),
  middleName: Yup.string().max(100, 'Too Long!'),//TODO do i not need an optional here?
  lastName: Yup.string().max(100, 'Too Long!').required('Required Last Name'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required email'),
  password: Yup.string().required('Please Enter your password').matches(PasswordRegex, PASSWORD_REQUIREMENTS),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .when('password', {
      is: (password: string) => !!(password && password.length > 0),
      then: Yup.string().oneOf([Yup.ref('password')], 'Passwords do not match'),
    }),
});

export type schemaValidationUserType = Yup.InferType<typeof schemaValidationUser>;

export const schemaValidationBasicCompany = Yup.object({
  company_legal_name: Yup.string().required('Required Company Name'),
  company_email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required Company Email'),
  company_physical_address_line1: Yup.string().required('Required Physical Address'),
  city: Yup.string().max(50, 'Too Long!').required('Required City'),
  state: Yup.string().required('Required State'),
  zip: Yup.string().min(5, 'Too short').max(5, 'Too Long').required('Required Zip'),
  country: Yup.string().required('Required Country'),
  website: Yup.string().matches(UrlRegex, 'URL is not valid'),
  firstName: Yup.string().max(100, 'Too Long!').required('Required First Name'),
  lastName: Yup.string().max(100, 'Too Long!').required('Required Last Name'),
  phone_number: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required email'),
  password: Yup.string().required('Please Enter your password').matches(PasswordRegex, PASSWORD_REQUIREMENTS),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .when('password', {
      is: (password: string) => !!(password && password.length > 0),
      then: Yup.string().oneOf([Yup.ref('password')], 'Passwords do not match'),
    }),
});

export type schemaValidationBasicCompanyType = Yup.InferType<typeof schemaValidationBasicCompany>;

export const schemaValidationGeneralCompanyInfo = Yup.object({
  company_legal_name: Yup.string().required('Required Company Name'),
  company_email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required Company Email'),
  company_physical_address_line1: Yup.string().required('Required Physical Address'),
  city: Yup.string().required('Required City'),
  state: Yup.string().max(50, 'Too Long!').required('Required State'),
  zip: Yup.string()
    .max(5, 'Too Long!') // anything longer than 5 causes an error on the api
    .min(5, 'Too short')
    .required('Required Zip'),
  country: Yup.string().required('Required Country'),
  website: Yup.string().matches(UrlRegex, 'URL is not valid'),
  phone_number: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
});

export type schemaValidationGeneralCompanyInfoType = Yup.InferType<typeof schemaValidationGeneralCompanyInfo>;

export const schemaValidationLegalCompany = Yup.object({
  company_legal_name: Yup.string().required('Required Legal Name'),
  company_DBA_name: Yup.string().required('Required DBA Name'),
  company_mailing_address: Yup.string().required('Required Mailing Address'),
  city: Yup.string().required('City'),
  state: Yup.string().required('Required State'),
  zip: Yup.string().min(5, 'Too short').max(5, 'Too Long').required('Zip'),
  country: Yup.string().required('Required Country'),
  state_of_incorporation: Yup.string().required('Required Incorporation State'),
  tax_ID: Yup.string().length(9, 'Tax Id must be exactly 9 digits.').matches(/^\d+$/, 'Tax Id should have digits only').required('Required Tax Id'),
  company_type: Yup.string().required('Required Company Type'),
});

export type schemaValidationLegalCompanyInfo = Yup.InferType<typeof schemaValidationLegalCompany>;

export const schemaValidationCompanyBankAccount = Yup.object({
  bank_name: Yup.string().required('Required Bank Name'),
  routing_number: Yup.string()
    .required('Required Routing Number')
    .matches(NumberRegex, 'Routing number must be digits.')
    .matches(BankRoutingNumberRegex, 'Routing number must start with a 0, 1, 2 or 3.')
    .length(9, 'Routing number must be exactly 9 digits.'),
  account_number: Yup.string().required('Required Account Number').matches(NumberRegex, 'Account number must be digits.').min(5, 'Account number must be at least 5 digits.'),
  bank_files: Yup.array().min(1, 'Required Bank Files').max(3, 'Maximum files are 3'),
});

export type schemaValidationCompanyBankAccountInfo = Yup.InferType<typeof schemaValidationCompanyBankAccount>;

export const schemaValidationPrincipalKYCdata = Yup.object({
  first_name: Yup.string().required('Required First Name'),
  last_name: Yup.string().required('Required Last Name'),
  middle_name: Yup.string(),
  dob: Yup.string().min(10, 'Format (MM/DD/YYYY)').max(10, 'Format (MM/DD/YYYY)').matches(DobRegex, 'Use Slashes(MM/DD/YYYY)').required('Required DOB(MM/DD/YYYY)'),
  title: Yup.string().required('Required Title'),
  ssn: Yup.string().length(9, 'SSN must be exactly 9 digits.').matches(/^\d+$/, 'SSN should have digits only').required('Required SSN'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
  phone: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  physical_address: Yup.string().required('Required Physical Address'),
  passport_number: Yup.string().required('Required ID'),
  city: Yup.string().required('Required'),
  state: Yup.string().required('Required City'),
  zip: Yup.string().min(5, 'Too short').max(5, 'Too Long').required('Required Zip'),
  country: Yup.string().required('Required Country'),
  ownership_amount: Yup.number().min(0, 'Below Range!').max(100, 'Exceeds Range!').required('Required'),
});

export type schemaValidationPrincipalKYCdataType = Yup.InferType<typeof schemaValidationPrincipalKYCdata>;

export const schemaValidationBeneficialOwnerData = Yup.object({
  first_name: Yup.string().required('Required First Name'),
  last_name: Yup.string().required('Required Last Name'),
  middle_name: Yup.string(),
  dob: Yup.string().min(10, 'Format (MM/DD/YYYY)').max(10, 'Format (MM/DD/YYYY)').matches(DobRegex, 'Use Slashes(MM/DD/YYYY)').required('Required DOB(MM/DD/YYYY)'),
  ssn: Yup.string().length(9, 'SSN must be exactly 9 digits.').matches(/^\d+$/, 'SSN should have digits only').required('Required SSN'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
  phone: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  physical_address: Yup.string().required('Required Physical Address'),
  city: Yup.string().required('Required City'),
  state: Yup.string().required('Required State'),
  zip: Yup.string().min(5, 'Too short').max(5, 'Too Long').required('Required Zip'),
  country: Yup.string().required('Required Country'),
  ownership_amount: Yup.number().min(25, 'Below Minimum Ownership for Beneficial Owner!').max(100, 'Exceeds Range!').required('Required'),
  passport_number: Yup.string().required('Required ID'),
});

export type schemaValidationBeneficialOwnerDataType = Yup.InferType<typeof schemaValidationBeneficialOwnerData>;

export const schemaValidationApplicantInfo = Yup.object({
  first_name: Yup.string().required('Required First Name'),
  last_name: Yup.string().required('Required Last Name'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required'),
});

export type schemaValidationApplicantInfoType = Yup.InferType<typeof schemaValidationApplicantInfo>;

export const schemaValidationCreateAnAccountModal = Yup.object({
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required email'),
  password: Yup.string().required('Please Enter your password').matches(PasswordRegex, PASSWORD_REQUIREMENTS),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .when('password', {
      is: (password: string) => !!(password && password.length > 0),
      then: Yup.string().oneOf([Yup.ref('password')], 'Passwords do not match'),
    }),
});

export type schemaValidationCreateAnAccountModalType = Yup.InferType<typeof schemaValidationCreateAnAccountModal>;

export const schemaValidationOwner = Yup.object({
  firstName: Yup.string().max(100, 'Too Long!').required('Required First Name'),
  lastName: Yup.string().max(100, 'Too Long!').required('Required Last Name'),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Required email'),
  phoneNumber: Yup.string().matches(E164PhoneRegex, 'Invalid Phone Number. Please enter in this format +[Country Code][Phone Number]').required('Required Phone Number'),
  // pmDistributionBankAccountUuid: Yup.string().required("Required Pm Distribution Bank Account"),
  // routingNumber: Yup.string()
  //   .required("Required Routing Number")
  //   .matches(NumberRegex, "Routing number must be digits.")
  //   .matches(BankRoutingNumberRegex, "Routing number must start with a 0, 1, 2 or 3.")
  //   .length(9, "Routing number must be exactly 9 digits."),
  // accountNumber: Yup.string()
  //   .required("Required Account Number")
  //   .matches(NumberRegex, "Account number must be digits.")
  //   .min(5, "Account number must be at least 5 digits."),
  // confirmAccountNumber: Yup.string()
  //   .required("Please confirm Account Number")
  //   .when("accountNumber", {
  //     is: (accountNumber: string) => !!(accountNumber && accountNumber.length > 0),
  //     then: Yup.string().oneOf([Yup.ref("accountNumber")], "Account Number doesn't match"),
  //   }),
});

export type schemaValidationOwnerType = Yup.InferType<typeof schemaValidationOwner>;

export const schemaValidationApplicantRegistration = Yup.object({
  firstName: Yup.string().max(100, 'Too Long!').required('First name is required'),
  lastName: Yup.string().max(100, 'Too Long!').required('Last name is required'),
  propertyAddress: Yup.string().max(100, 'Too Long!').required('Property address required'),
  locationUuid: Yup.string().nullable(),
  email: Yup.string().matches(EmailRegex, 'Please type a valid Email.').required('Email required'),
  password: Yup.string().required('Please Enter your password').matches(PasswordRegex, PASSWORD_REQUIREMENTS),
  confirmPassword: Yup.string()
    .required('Please confirm your password')
    .when('password', {
      is: (password: string) => !!(password && password.length > 0),
      then: Yup.string().oneOf([Yup.ref('password')], 'Passwords do not match'),
    }),
});

export type schemaValidationApplicantRegistrationType = Yup.InferType<typeof schemaValidationApplicantRegistration>;

export const schemaValidationApplicationRentalHistory = Yup.object({
  Line1: Yup.string().required('Please enter a street address'),
  Line2: Yup.string().nullable(),
  City: Yup.string().required('Please enter a city'),
  State: Yup.string().required('Please select a state'),
  Zip: Yup.string().min(5, 'Please enter a five digit zip').max(5, 'Please enter a five digit zip').required('Please enter a zip'),
  Country: Yup.string().required('Please select a country'),
  IsCurrentAddress: Yup.boolean().required(),
  LandlordName: Yup.string().required("Please enter the landlord's name"),
  LandlordPhone: Yup.string().min(12, 'Format XXX-XXX-XXXX').max(12, 'Format XXX-XXX-XXXX').matches(PhoneRegexAlt, 'Use Slashes(XXX-XXX-XXXX)').nullable(),
  LandlordEmail: Yup.string().matches(EmailRegex, 'Please type a valid Email.').nullable(),
  LandlordMonthlyRentAmount: Yup.string().nullable(),
  MoveInDate: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .required('Please enter a move in date')
    .test({
      name: 'Move In Date Check',
      exclusive: true,
      message: 'Move In Date should be less than today',
      test: (value, testContext) => {
        const returnObj = testContext.parent.MoveInDate && new Date(testContext.parent.MoveInDate) > new Date();
        return !returnObj;
      },
    })
    .test({
      name: 'Move In Date Check with Move Out Date',
      exclusive: true,
      message: 'Move In Date should be less than Move Out Date',
      test: (value, testContext) => {
        const returnObj = testContext.parent.MoveInDate && testContext.parent.MoveOutDate && new Date(testContext.parent.MoveInDate) > new Date(testContext.parent.MoveOutDate);
        return !returnObj;
      },
    }),
  MoveOutDate: Yup.string()
    .min(10, 'Please enter a date in this format MM/DD/YYYY')
    .max(10, 'Please enter a date in this format MM/DD/YYYY')
    .matches(DobRegex, 'Please enter a date in this format MM/DD/YYYY')
    .nullable()
    .test({
      name: 'Move Out Date Check',
      exclusive: true,
      message: 'Move Out Date should be less than today',
      test: (value, testContext) => {
        const returnObj = testContext.parent.MoveOutDate && new Date(testContext.parent.MoveOutDate) > new Date();
        return !returnObj;
      },
    })
    .test({
      name: 'Move Out Date Check with Move In Date',
      exclusive: true,
      message: 'Move Out Date should be greater than Move In Date',
      test: (value, testContext) => {
        const returnObj = testContext.parent.MoveInDate && testContext.parent.MoveOutDate && new Date(testContext.parent.MoveInDate) > new Date(testContext.parent.MoveOutDate);
        return !returnObj;
      },
    }),
});

export type schemaValidationApplicationRentalHistoryType = Yup.InferType<typeof schemaValidationApplicationRentalHistory>;

export const schemaValidationApplicationPet = Yup.object({
  Species: Yup.string().max(45, 'Max length is 45 characters').nullable(),
  Name: Yup.string().max(45, 'Max length is 45 characters').nullable(),
  Breed: Yup.string().max(45, 'Max length is 45 characters').nullable(),
  WeightLbs: Yup.string().min(0).nullable(),
  LivesOnPremises: Yup.string().nullable(),
});

export type schemaValidationApplicationPetType = Yup.InferType<typeof schemaValidationApplicationPet>;

export const schemaValidationApplicationVehicle = Yup.object({
  Type: Yup.string().max(45, 'Max length is 45 characters').required('Please select a type'),
  Make: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a make'),
  Model: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a model'),
  Year: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a year'),
  Color: Yup.string().max(45, 'Max length is 45 characters').required('Please enter a color'),
  LicensePlateNo: Yup.string().nullable(),
  Length: Yup.string().nullable(),
});

export type schemaValidationApplicationVehicleType = Yup.InferType<typeof schemaValidationApplicationVehicle>;

export const schemaValidationBankDetails = Yup.object({
  NickName: Yup.string().nullable(),
  BankName: Yup.string().required('Required Bank Name'),
  AccountType: Yup.string().default('C'),
  RoutingNumber: Yup.string()
    .required('Required Routing Number')
    .matches(NumberRegex, 'Routing number must be digits.')
    .matches(BankRoutingNumberRegex, 'Routing number must start with a 0, 1, 2 or 3.')
    .length(9, 'Routing number must be exactly 9 digits.'),
  ConfirmRoutingNumber: Yup.string()
    .required('Please confirm Routing Number')
    .when('RoutingNumber', {
      is: (routingNumber: string) => !!(routingNumber && routingNumber.length > 0),
      then: Yup.string().oneOf([Yup.ref('RoutingNumber')], "Routing Number doesn't match"),
    }),
  AccountNumber: Yup.string().required('Required Account Number').matches(NumberRegex, 'Account number must be digits.').min(5, 'Account number must be at least 5 digits.'),
  ConfirmAccountNumber: Yup.string()
    .required('Please confirm Account Number')
    .when('AccountNumber', {
      is: (accountNumber: string) => !!(accountNumber && accountNumber.length > 0),
      then: Yup.string().oneOf([Yup.ref('AccountNumber')], "Account Number doesn't match"),
    }),
});

export type schemaValidationBankDetailsType = Yup.InferType<typeof schemaValidationBankDetails>;

export const schemaValidationBankAccountOwnership = Yup.object({
  BankHolderName: Yup.string()
    .required('Required Bank Holder Name')
    .test({
      name: 'NameCheck',
      exclusive: true,
      message: 'First and last name are required.',
      test: (value) => !!value && value.split(' ').length > 1,
    }),
  City: Yup.string().required('Required City'),
  State: Yup.string().required('Required State'),
  Zip: Yup.string().required('Required Zip'),
  Line1: Yup.string().required('Required Address'),
  Country: Yup.string().required('Required Country'),
});

export type schemaValidationBankAccountOwnershipType = Yup.InferType<typeof schemaValidationBankAccountOwnership>;

export const schemaValidationBankCard = Yup.object({
  Title: Yup.string().nullable(),
  CardNetwork: Yup.string().required('Required CardNetwork'),
  CardType: Yup.string().required('Required CardType'),
  CardNumber: Yup.string()
    .min(13, 'Card number is incomplete')
    .required('Required Card Number')
    .test({
      name: 'Credit CardType Check',
      exclusive: true,
      message: 'You have entered a Debit Card number, Please enter a valid Credit Card number.',
      test: (value, testContext) => {
        const returnObj = testContext.parent.CardType === 'DB' && testContext.parent.Title?.includes('Credit');
        return !returnObj;
      },
    })
    .test({
      name: 'Debit CardType Check',
      exclusive: true,
      message: 'You have entered a Credit Card number, Please enter a valid Debit Card number.',
      test: (value, testContext) => {
        const returnObj = testContext.parent.CardType === 'CC' && testContext.parent.Title?.includes('Debit');
        return !returnObj;
      },
    })
    .when('CardNetwork', {
      is: (cardNetwork: string) => cardNetwork === 'visa' || cardNetwork === 'mastercard',
      then: Yup.string().length(16, 'Card should be 16 digits'),
    })
    .when('CardNetwork', {
      is: (cardNetwork: string) => cardNetwork === 'amex',
      then: Yup.string().length(15, 'Card should be 15 digits'),
    }),
  SecurityCode: Yup.string().required('Required Card CVV Code'),
  ExpirationDate: Yup.string().required('Required Expiration Date').matches(cardExpirationDateRegex, 'Expiration date needs to be in this format: MM/YYYY'),
  CardholderName: Yup.string()
    .required('Required Card Holder Name')
    .test({
      name: 'NameCheck',
      exclusive: true,
      message: 'First and last name are required.',
      test: (value) => !!value && value.split(' ').length > 1,
    }),
  NickName: Yup.string().nullable(),
  City: Yup.string().required('Required City'),
  State: Yup.string().required('Required State'),
  Zip: Yup.string().required('Required Zip'),
  Line1: Yup.string().required('Required Address'),
  Country: Yup.string().required('Required Country'),
});

export type schemaValidationBankCardType = Yup.InferType<typeof schemaValidationBankCard>;
